import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "@geoman-io/leaflet-geoman-free";
import "@geoman-io/leaflet-geoman-free/dist/leaflet-geoman.css";
import {
  BoxSelect,
  ExternalLink,
  Info,
  Maximize2,
  MessageSquare,
  Minimize2,
  Move,
  Pencil,
  PersonStanding,
  Replace,
  Ruler,
  Search,
  SlidersHorizontal,
  Spline,
  Trash2,
  Type,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/Button";
import { geocodeAddress, getGeoportalUrl, getUldkParcelGeom } from "../api";
import type {
  GeoJsonGeometry,
  ZEDrawingFeatureCollection,
  ZEElementKind,
  ZEElementType,
  ZEParamSpec,
  ZEParamValue,
  ZEMapAnnotation,
  ZEProtectedAreasGeom,
  ZEWaterCrossingsGeom,
  ZETenderParcel,
  ZETenderParcelDraft,
} from "../types";
import {
  areaStyleFor,
  colorForType,
  elementSwatchSvg,
  findOwnershipOption,
  fmtMeters,
  lineStyleFor,
  objectSvg,
  ownershipCodeColor,
  protectedAreaColor,
} from "../utils";
import {
  type ClosedAreasLayer,
  createGugikLayers,
  GugikLayerPanel,
  useGugikLoading,
  useGugikState,
  useGugikToggle,
  fetchNidMonuments,
  nidMonumentsHtml,
  useNidTileContent,
} from "@/components/map/gugik";
import { detachRemovedLayer, hasVertices, isEmptyGeometry } from "@/components/map/geometry";
import { usePersistentToggle } from "@/hooks/usePersistentToggle";
import { ZEAnnotationsPanel } from "./ZEAnnotationsPanel";

type EditMode = "draw" | "edit" | "drag" | "remove" | "label" | "params" | "retype" | "annotate" | "info" | "off" | "streetview";

// Result of the "Znajdź na mapie" tool — either a geocoded address (point) or a parcel
// (polygon by TERYT). Kept so the panel can restore the last hit when reopened.
type LocateResult =
  | { kind: "address"; lat: number; lng: number; label: string }
  | { kind: "parcel"; teryt: string; geometry: GeoJsonGeometry };

// ── Parcel popup HTML (Leaflet popup = outside React, inline styles only) ────
function fmtM2(m2: number | null | undefined) {
  if (m2 == null) return null;
  return m2 >= 10_000
    ? `${(m2 / 10_000).toLocaleString("pl-PL", { maximumFractionDigits: 2 })} ha`
    : `${Math.round(m2).toLocaleString("pl-PL")} m²`;
}

function headingBetween(from: L.LatLng, to: L.LatLng): number {
  const dLon = ((to.lng - from.lng) * Math.PI) / 180;
  const lat1 = (from.lat * Math.PI) / 180;
  const lat2 = (to.lat * Math.PI) / 180;
  const y = Math.sin(dLon) * Math.cos(lat2);
  const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLon);
  return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
}

function buildParcelPopupHtml(p: ZETenderParcel, draft: ZETenderParcelDraft | undefined): string {
  const uldkOpt   = findOwnershipOption(p.ownership_code);
  const ovOpt     = draft?.ownership_override ? findOwnershipOption(draft.ownership_override) : undefined;
  const uldkColor = ownershipCodeColor(p.ownership_code);
  const ovColor   = ovOpt?.color ?? uldkColor;

  const row = (label: string, val: string, bold = false, color?: string) =>
    `<tr>
      <td style="color:#6b7280;padding:2px 8px 2px 0;white-space:nowrap;font-size:10px">${label}</td>
      <td style="text-align:right;padding:2px 0;${bold ? "font-weight:700;" : ""}${color ? `color:${color};` : ""}font-size:11px">${val}</td>
    </tr>`;

  const swatch = (color: string) =>
    `<span style="display:inline-block;width:9px;height:9px;background:${color};border:1px solid rgba(0,0,0,.18);vertical-align:middle;margin-right:3px;flex-shrink:0"></span>`;

  const breakdown = p.element_breakdown.length
    ? `<div style="margin-top:8px;padding-top:6px;border-top:1px solid #e5e7eb;display:flex;flex-wrap:wrap;gap:3px">` +
      p.element_breakdown.map((b) => {
        const dot = b.kind === "line" ? "#2563eb" : "#ea580c";
        const val = b.length_m != null ? `${Math.round(b.length_m)} m` : `${b.count} szt`;
        return `<span style="display:inline-flex;align-items:center;gap:3px;background:#f3f4f6;border:1px solid #e5e7eb;padding:1px 5px;font-size:10px;border-radius:2px">
          <span style="width:6px;height:6px;background:${dot};flex-shrink:0"></span>
          <span style="color:#6b7280">${b.label}</span>
          <span style="font-weight:700">${val}</span>
        </span>`;
      }).join("") + `</div>`
    : "";

  const wpwCount = draft?.wpw_count;
  const effectiveOpt = ovOpt ?? uldkOpt;
  const showWpw = effectiveOpt?.isWpw && wpwCount != null;

  return `
    <div style="min-width:210px;max-width:290px;font-family:ui-monospace,monospace;line-height:1.4">
      <div style="font-weight:700;font-size:13px;color:#111;padding-bottom:5px;margin-bottom:5px;border-bottom:2px solid #e5e7eb">${p.teryt}</div>
      ${p.obreb ? `<div style="font-size:10px;color:#9ca3af;margin-bottom:6px">${p.obreb}</div>` : ""}
      <table style="width:100%;border-collapse:collapse">
        ${p.area_m2 ? row("Pow. działki", fmtM2(p.area_m2) ?? "") : ""}
        ${p.intersection_length_m != null ? row("Trasa w działce", `${Math.round(p.intersection_length_m)} m`, true, "#dc2626") : ""}
        <tr>
          <td style="color:#6b7280;padding:2px 8px 2px 0;white-space:nowrap;font-size:10px">Własność ULDK</td>
          <td style="text-align:right;padding:2px 0;font-size:11px">${swatch(uldkColor)}${(uldkOpt?.label ?? p.ownership_code) || "—"}</td>
        </tr>
        ${ovOpt ? `<tr>
          <td style="color:#6b7280;padding:2px 8px 2px 0;white-space:nowrap;font-size:10px">Nadpisanie</td>
          <td style="text-align:right;padding:2px 0;font-weight:700;font-size:11px;color:${ovColor}">${swatch(ovColor)}${ovOpt.label}</td>
        </tr>` : ""}
        ${showWpw ? row("Właściciele WPW", `~${wpwCount}`, true, ovColor) : ""}
      </table>
      ${breakdown}
    </div>`;
}

function layerLengthM(layer: L.Layer): number {
  if (!(layer instanceof L.Polyline)) return 0;
  const raw = layer.getLatLngs();
  const lls: L.LatLng[] = Array.isArray(raw[0])
    ? (raw as L.LatLng[][]).flat()
    : (raw as L.LatLng[]);
  let total = 0;
  for (let i = 1; i < lls.length; i++) total += lls[i - 1].distanceTo(lls[i]);
  return total;
}

type FeatureLayer = L.Layer & { feature?: GeoJSON.Feature };

/** A feature carrying no coordinate at any depth — nothing a user could have drawn. */
const isEmptyFeature = (f: ZEDrawingFeatureCollection["features"][number]) =>
  isEmptyGeometry(f.geometry);

/** Features carrying no coordinates at all — corrupt rows that render nothing. */
export function countEmptyFeatures(fc: ZEDrawingFeatureCollection | null | undefined): number {
  return (fc?.features ?? []).filter(isEmptyFeature).length;
}

/**
 * Strip coordinate-less features before the drawing leaves the editor.
 *
 * Geoman is the source of these. Removing a line's vertices one by one ends in
 * `Edit.Line._removeMarker` calling `this._layer.remove()` — Leaflet's own `remove()`,
 * which detaches the layer from the MAP but leaves it in the FeatureGroup we own, now
 * holding zero latlngs. It looks deleted, and `group.toGeoJSON()` still emits it as
 * `{"type": "LineString", "coordinates": []}`. Saved like that, it came back on the next
 * load as a blank Polyline and crashed the label pass (`getCenter()` throws on an empty
 * ring), taking the whole page down.
 *
 * Verified against @geoman-io/leaflet-geoman-free 2.19 — see the regression test.
 */
export function dropEmptyFeatures(
  fc: ZEDrawingFeatureCollection,
): ZEDrawingFeatureCollection {
  const kept = (fc.features ?? []).filter((f) => !isEmptyFeature(f));
  return kept.length === (fc.features ?? []).length ? fc : { ...fc, features: kept };
}

/**
 * `L.geoJSON` on geometry it does not recognise, or with a non-finite coordinate, THROWS
 * ("Invalid GeoJSON object.", "Invalid LatLng object: (NaN, NaN)"). Every call here runs
 * inside an effect, so an unguarded throw takes down the whole route, not just the layer.
 *
 * One bad overlay must cost its own overlay and nothing else: log it and carry on drawing
 * the rest of the map.
 */
function safeGeoJson(
  geometry: unknown,
  options: L.GeoJSONOptions,
  what: string,
): L.GeoJSON | null {
  if (!geometry) return null;
  try {
    return L.geoJSON(geometry as GeoJSON.GeoJsonObject, options);
  } catch (err) {
    console.error(`ZEDrawingMap: pominięto warstwę „${what}” — nieprawidłowa geometria`, err, geometry);
    return null;
  }
}

/** User label lands in tooltip HTML — escape it (newlines become <br> separately). */
function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function layerLabel(layer: L.Layer): string {
  const raw = (layer as FeatureLayer).feature?.properties?.label;
  return typeof raw === "string" ? raw : "";
}

function layerAttrs(layer: L.Layer): Record<string, ZEParamValue> {
  const raw = (layer as FeatureLayer).feature?.properties?.attrs;
  return raw && typeof raw === "object" && !Array.isArray(raw)
    ? (raw as Record<string, ZEParamValue>)
    : {};
}

function layerTypeCode(layer: L.Layer): string {
  const raw = (layer as FeatureLayer).feature?.properties?.element_type;
  return typeof raw === "string" ? raw : "";
}

/** Element group inferred from the LAYER's geometry, not from its current type — the
 *  geometry is what stays after a retype, and the backend validates kind against it.
 *  Polygon extends Polyline, so the area check must come first. */
function layerKind(layer: L.Layer): ZEElementKind {
  if (layer instanceof L.Marker) return "object";
  if (layer instanceof L.Polygon) return "area";
  return "line";
}

/** Any vertex (or the marker point) inside the rectangle counts as "in the area" —
 *  cheap and matches how users aim the box; bbox-intersection alone would also catch
 *  long lines that merely pass nearby without entering the rectangle. */
function layerInBounds(layer: L.Layer, bounds: L.LatLngBounds): boolean {
  if (layer instanceof L.Marker) return bounds.contains(layer.getLatLng());
  if (layer instanceof L.Polyline) {
    const raw = layer.getLatLngs() as unknown as (L.LatLng | L.LatLng[] | L.LatLng[][])[];
    return (raw.flat(2) as L.LatLng[]).some((ll) => bounds.contains(ll));
  }
  return false;
}

/** Stan grupowej zmiany typu (tylko tryb „Typ"): po narysowaniu prostokąta selekcji
 *  albo od razu wybieramy nowy typ (jeden typ w obszarze), albo najpierw użytkownik
 *  wskazuje, KTÓRY typ zaznaczyć (kilka typów w obszarze). */
type GroupRetypeState =
  | { step: "empty" }
  | { step: "pickType"; groups: { code: string; layers: FeatureLayer[] }[] }
  | { step: "target"; code: string; layers: FeatureLayer[] };

/** Wartość parametru wpisana w pole formularza. Puste = „bez zdania", czyli wracamy do
 *  domyślnej wariantu — dlatego kasowanie pola musi USUWAĆ klucz, a nie zapisywać "". */
function parseParamInput(spec: ZEParamSpec, raw: string): ZEParamValue | undefined {
  if (raw === "") return undefined;
  if (spec.type === "number") {
    const n = Number(raw.replace(",", "."));
    return Number.isFinite(n) ? n : undefined;
  }
  if (spec.type === "bool") return raw === "true";
  return raw;
}

/**
 * Parametry techniczne jednego narysowanego elementu.
 *
 * Każde pole ma trzy stany, i ta różnica jest sensem panelu: własna wartość elementu,
 * odziedziczona domyślna wariantu, albo domyślna ze słownika. Puste pole nie znaczy
 * „zero" tylko „bez zdania" — dlatego przy każdym polu widać, co element odziedziczy,
 * a „Wyczyść" oddaje wartość z powrotem domyślnym zamiast wpisywać pustkę.
 */
function ParamsEditorPanel({
  layer,
  elementType,
  defaults,
  onCommit,
  onClose,
}: {
  layer: FeatureLayer;
  elementType: ZEElementType | undefined;
  defaults: Record<string, ZEParamValue>;
  onCommit: (spec: ZEParamSpec, raw: string) => void;
  onClose: () => void;
}) {
  const attrs = layerAttrs(layer);
  const schema = elementType?.param_schema ?? [];

  const inheritedOf = (spec: ZEParamSpec): ZEParamValue =>
    spec.key in defaults ? defaults[spec.key] : (spec.default ?? null);

  const describe = (spec: ZEParamSpec, value: ZEParamValue): string => {
    if (value === null || value === undefined || value === "") return "—";
    if (spec.type === "bool") return value ? "tak" : "nie";
    if (spec.type === "choice") {
      const hit = spec.choices?.find((c) => c.value === String(value));
      return hit?.label ?? String(value);
    }
    return `${value}${spec.unit ? ` ${spec.unit}` : ""}`;
  };

  return (
    <div className="absolute top-2 left-1/2 z-[1100] w-96 -translate-x-1/2 border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-2 shadow-lg">
      <div className="mb-1.5 flex items-center justify-between">
        <span className="font-mono text-[10px] uppercase tracking-wider text-[var(--color-text-secondary)]">
          Parametry: {elementType?.label ?? "element"}
        </span>
        <button
          className="text-[11px] text-[var(--color-text-tertiary)] hover:text-[var(--color-text-primary)]"
          onClick={onClose}
        >
          Zamknij
        </button>
      </div>

      {schema.length === 0 ? (
        <p className="py-2 text-[12px] text-[var(--color-text-muted)]">
          Ten element nie ma czego doprecyzowywać — liczy się sama geometria.
        </p>
      ) : (
        <div className="max-h-[380px] space-y-2 overflow-y-auto pr-1">
          {schema.map((spec) => {
            const own = attrs[spec.key];
            const hasOwn = spec.key in attrs;
            const inherited = inheritedOf(spec);
            const raw = hasOwn ? String(own ?? "") : "";
            return (
              <div key={spec.key}>
                <div className="flex items-baseline justify-between gap-2">
                  <label className="text-[11px] text-[var(--color-text-secondary)]">
                    {spec.label}
                    {spec.unit && spec.type === "number" ? ` [${spec.unit}]` : ""}
                  </label>
                  <span
                    className={`font-mono text-[10px] ${hasOwn ? "text-amber-600" : "text-[var(--color-text-tertiary)]"}`}
                  >
                    {hasOwn ? "własna" : `dziedziczy: ${describe(spec, inherited)}`}
                  </span>
                </div>
                <div className="mt-0.5 flex items-center gap-1.5">
                  {spec.type === "choice" ? (
                    <select
                      className="input flex-1 py-1 text-[12px]"
                      value={raw}
                      onChange={(e) => onCommit(spec, e.target.value)}
                    >
                      <option value="">— jak domyślnie —</option>
                      {(spec.choices ?? []).map((c) => (
                        <option key={c.value} value={c.value}>
                          {c.label}
                        </option>
                      ))}
                    </select>
                  ) : spec.type === "bool" ? (
                    <select
                      className="input flex-1 py-1 text-[12px]"
                      value={hasOwn ? String(Boolean(own)) : ""}
                      onChange={(e) => onCommit(spec, e.target.value)}
                    >
                      <option value="">— jak domyślnie —</option>
                      <option value="true">tak</option>
                      <option value="false">nie</option>
                    </select>
                  ) : (
                    <input
                      type="number"
                      step="any"
                      className="input flex-1 py-1 font-mono text-[12px]"
                      value={raw}
                      placeholder={String(inherited ?? "")}
                      onChange={(e) => onCommit(spec, e.target.value)}
                    />
                  )}
                  {hasOwn && (
                    <button
                      className="text-[11px] text-[var(--color-text-tertiary)] hover:text-[var(--color-primary)]"
                      title="Wróć do wartości domyślnej"
                      onClick={() => onCommit(spec, "")}
                    >
                      Wyczyść
                    </button>
                  )}
                </div>
                {spec.help && (
                  <p className="mt-0.5 text-[10px] text-[var(--color-text-tertiary)]">
                    {spec.help}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/**
 * Zmiana typu narysowanego elementu bez przerysowywania. Oferujemy wyłącznie typy tej
 * samej grupy (linia/powierzchnia/obiekt), bo geometria zostaje — backend odrzuca np.
 * punkt zapisany pod typem liniowym. Parametry techniczne elementu zostają na miejscu:
 * przy zapisie backend odfiltruje klucze spoza schematu nowego typu.
 */
function RetypeEditorPanel({
  title,
  kind,
  currentCode,
  elementTypes,
  onSelect,
  onClose,
}: {
  title: string;
  kind: ZEElementKind;
  currentCode: string;
  elementTypes: ZEElementType[];
  onSelect: (t: ZEElementType) => void;
  onClose: () => void;
}) {
  const options = elementTypes.filter((t) => t.kind === kind);
  const kindLabel =
    kind === "line" ? "Linie (trasa)" : kind === "area" ? "Powierzchnie (odtworzenie)" : "Obiekty (punkty)";
  const hasAlternatives = options.some((t) => t.code !== currentCode);

  return (
    <div className="absolute top-2 left-1/2 z-[1100] w-96 -translate-x-1/2 border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-2 shadow-lg">
      <div className="mb-1.5 flex items-center justify-between">
        <span className="font-mono text-[10px] uppercase tracking-wider text-[var(--color-text-secondary)]">
          {title}
        </span>
        <button
          className="text-[11px] text-[var(--color-text-tertiary)] hover:text-[var(--color-text-primary)]"
          onClick={onClose}
        >
          Zamknij
        </button>
      </div>
      {!hasAlternatives ? (
        <p className="py-2 text-[12px] text-[var(--color-text-muted)]">
          Brak innych typów w grupie „{kindLabel}" — nie ma na co zmienić.
        </p>
      ) : (
        <>
          <p className="section-label mb-1">{kindLabel}</p>
          <div className="flex max-h-[380px] flex-wrap gap-1.5 overflow-y-auto pr-1">
            {options.map((t) => {
              const active = t.code === currentCode;
              return (
                <button
                  key={t.id}
                  type="button"
                  disabled={active}
                  title={active ? "Obecny typ elementu" : `Zmień na: ${t.label}`}
                  onClick={() => onSelect(t)}
                  className={`flex items-center gap-1.5 px-2 py-1 border text-[11px] font-mono transition-colors ${
                    active
                      ? "border-[var(--color-primary)] bg-[var(--color-bg-elevated)] text-[var(--color-text-primary)] cursor-default"
                      : "border-[var(--color-border)] text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-elevated)]"
                  }`}
                >
                  <span
                    className="inline-flex w-6 shrink-0 items-center justify-center"
                    aria-hidden
                    dangerouslySetInnerHTML={{ __html: elementSwatchSvg(t) }}
                  />
                  <span className="text-left">{t.label}</span>
                  <span className="text-[var(--color-text-muted)]">· {t.unit}</span>
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

export interface ZEDrawingMapHandle {
  fitToParcel: (teryt: string) => void;
  getCenter: () => { lat: number; lng: number } | null;
  /** Fly the map to an annotation by id (no-op if annotation not in current set). */
  flyToAnnotation: (id: string) => void;
}

interface Props {
  elementTypes: ZEElementType[];
  activeType: ZEElementType | null;
  initialDrawing: ZEDrawingFeatureCollection | null | undefined;
  parcels: ZETenderParcel[];
  onChange: (fc: ZEDrawingFeatureCollection, dirty: boolean) => void;
  isFullscreen: boolean;
  onToggleFullscreen: () => void;
  fitTrigger?: number;
  onParcelClick?: (teryt: string) => void;
  drafts?: ZETenderParcelDraft[];
  mpzpGapGeom?: GeoJsonGeometry | null;
  mpzpUnknownGeom?: GeoJsonGeometry | null;
  /** GDOŚ protected areas the route cuts — corridors colored per form type. */
  protectedAreasGeom?: ZEProtectedAreasGeom | null;
  /** Przejścia trasy przez wody (Hydroportal) — punkty, kolor wg pozycji cennika. */
  waterCrossingsGeom?: ZEWaterCrossingsGeom | null;
  /** Loaded annotation pins — rendered as a separate layer above the drawing. */
  annotations?: ZEMapAnnotation[];
  /** Called when the user confirms a new pin with text. */
  onAnnotationCreate?: (lat: number, lng: number, text: string) => Promise<ZEMapAnnotation>;
  /** Called when the user edits/moves an existing pin. */
  onAnnotationUpdate?: (id: string, payload: { text?: string; geometry?: { type: string; coordinates: [number, number] } }) => void;
  /** Called when the user deletes an annotation. */
  onAnnotationDelete?: (id: string) => void;
  /**
   * When set to an annotation (from an external panel), the map opens that
   * annotation's edit dialog and flies to its pin. Set back to null after use.
   */
  externalAnnotationEdit?: ZEMapAnnotation | null;
  onExternalAnnotationEditHandled?: () => void;
  /** Domyślne parametry obmiaru wariantu — pokazywane w edytorze parametrów jako to,
   *  co element odziedziczy, gdy nie ma własnej wartości. */
  takeoffDefaults?: Record<string, ZEParamValue>;
}

/**
 * Interactive Leaflet + Leaflet-Geoman drawing surface for one ZE variant. The user
 * draws lines (route element types) and points (object element types); every feature is
 * tagged with `properties.element_type = code`. The full drawing is serialized to a
 * GeoJSON FeatureCollection (EPSG:4326) via `drawnGroup.toGeoJSON()` and lifted up.
 */
export const ZEDrawingMap = forwardRef<ZEDrawingMapHandle, Props>(function ZEDrawingMap({
  elementTypes,
  activeType,
  initialDrawing,
  parcels,
  onChange,
  isFullscreen,
  onToggleFullscreen,
  fitTrigger,
  onParcelClick,
  drafts,
  mpzpGapGeom,
  mpzpUnknownGeom,
  protectedAreasGeom,
  waterCrossingsGeom,
  annotations = [],
  onAnnotationCreate,
  onAnnotationUpdate,
  onAnnotationDelete,
  externalAnnotationEdit,
  onExternalAnnotationEditHandled,
  takeoffDefaults,
}, ref) {
  const typesByCode = useMemo(() => {
    const m: Record<string, ZEElementType> = {};
    for (const t of elementTypes) m[t.code] = t;
    return m;
  }, [elementTypes]);

  const mapRef = useRef<L.Map | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const osmRef = useRef<L.TileLayer | null>(null);
  const drawnGroupRef = useRef<L.FeatureGroup | null>(null);
  const parcelsRef = useRef<L.LayerGroup | null>(null);
  const parcelsRendererRef = useRef<L.Canvas | null>(null);
  const gugikParcelsRef = useRef<L.TileLayer.WMS | null>(null);
  const gugikInfraRef = useRef<L.TileLayer.WMS | null>(null);
  const gugikOrthoRef = useRef<L.TileLayer.WMS | null>(null);
  const gugikMpzpRef = useRef<L.TileLayer.WMS | null>(null);
  const gugikGdosRef = useRef<L.TileLayer.WMS | null>(null);
  const gugikKonserwatorRef = useRef<L.TileLayer.WMS | null>(null);
  const gugikKonserwatorEwidencjaRef = useRef<L.TileLayer.WMS | null>(null);
  const gugikClosedAreasRef = useRef<ClosedAreasLayer | null>(null);
  const mpzpGapLayerRef = useRef<L.GeoJSON | null>(null);
  const mpzpUnknownLayerRef = useRef<L.GeoJSON | null>(null);
  const protectedAreasLayerRef = useRef<L.GeoJSON | null>(null);
  const waterCrossingsLayerRef = useRef<L.GeoJSON | null>(null);
  const activeTypeRef = useRef<ZEElementType | null>(null);
  const typesByCodeRef = useRef(typesByCode);
  typesByCodeRef.current = typesByCode;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const onParcelClickRef = useRef(onParcelClick);
  onParcelClickRef.current = onParcelClick;
  // teryt → bounds map, rebuilt when parcels layer refreshes — used for fit-to-parcel.
  const parcelBoundsRef = useRef<Map<string, L.LatLngBounds>>(new Map());
  // Temporary highlight layer shown after Przybliż — dashed outline, auto-removed.
  const highlightLayerRef = useRef<L.Layer | null>(null);
  const highlightTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Which feature-group we've already loaded `initialDrawing` into. Guarding on the GROUP
  // INSTANCE (not a boolean) is deliberate: under React StrictMode a mount runs
  // setup→cleanup→setup and refs persist across it, so the first setup loads into a map that
  // the cleanup then discards while a boolean "loaded" flag stays true — the rebuilt map ends
  // up empty (only visible on a cached re-visit, where initialDrawing is ready during the
  // double-invoke). Comparing the group instance reloads into the rebuilt group, yet still
  // skips background refetches of the SAME group so unsaved edits are never wiped.
  const loadedIntoRef = useRef<L.FeatureGroup | null>(null);
  // One-shot flag: have we auto-fitted the initial view yet? Set true after the first
  // successful fit so subsequent parcel refreshes (draft ownership changes etc.) don't
  // re-center the map.
  const autoFittedRef = useRef(false);
  // Separate layer group for annotation pins — kept above the drawing but in its own
  // pane so annotations never interfere with Geoman drawing tools.
  const annotationsGroupRef = useRef<L.LayerGroup | null>(null);
  // annotation id → marker (for flyToAnnotation and live re-render on prop change).
  const annotationMarkersRef = useRef<Map<string, L.Marker>>(new Map());
  const annotationsRef = useRef(annotations);
  annotationsRef.current = annotations;
  const onAnnotationCreateRef = useRef(onAnnotationCreate);
  onAnnotationCreateRef.current = onAnnotationCreate;
  const onAnnotationUpdateRef = useRef(onAnnotationUpdate);
  onAnnotationUpdateRef.current = onAnnotationUpdate;
  const onAnnotationDeleteRef = useRef(onAnnotationDelete);
  onAnnotationDeleteRef.current = onAnnotationDelete;

  const [mode, setMode] = useState<EditMode>("draw");
  const modeRef = useRef<EditMode>("draw");
  // GUGiK reference overlays (działki + GESUT + ortofoto + MPZP) — all ON by default so the
  // reference layers are visible without hunting the panel; `zoom` drives the panel hints, and
  // each layer's own minZoom keeps it from firing blank requests at low zoom (see gugik.tsx).
  const [gugik, setGugik] = useGugikState("zeTenders.drawing", {
    parcels: true,
    infra: true,
    ortho: true,
    mpzp: false,
    gdos: false,
    konserwator: false,
    konserwatorEwidencja: false,
    closedAreas: false,
  });
  // Latest overlay toggles for the "info" click handler (armed once per mode switch).
  const gugikStateRef = useRef(gugik);
  useEffect(() => {
    gugikStateRef.current = gugik;
  }, [gugik]);
  // Set when the stored drawing could not be parsed — the map then shows at most a partial
  // route, and saving is held back so the truncated version never reaches the server.
  const [drawingLoadFailed, setDrawingLoadFailed] = useState(false);
  // Coordinate-less features already sitting in the stored drawing — Geoman leftovers from
  // before `dropEmptyFeatures` existed. They draw nothing; the next save clears them out.
  const [emptyFeatureCount, setEmptyFeatureCount] = useState(0);
  const [zoom, setZoom] = useState(6);
  const [scaleRatio, setScaleRatio] = useState<string>("");
  const [drawingLengthM, setDrawingLengthM] = useState<number | null>(null);
  // Display preference ("Długości" on the toolbar) — survives remounts like the GUGiK panel.
  const [showLineLengths, setShowLineLengths] = usePersistentToggle(
    "zeTenders.drawing.showLineLengths",
    false,
  );
  const showLineLengthsRef = useRef(false);
  showLineLengthsRef.current = showLineLengths;
  // Feature whose label is being edited in the on-map editor (only while mode === "label").
  const [labelEditor, setLabelEditor] = useState<{ layer: FeatureLayer; text: string } | null>(null);
  // Feature whose technical parameters are being edited (only while mode === "params").
  const [paramsEditor, setParamsEditor] = useState<{ layer: FeatureLayer } | null>(null);
  // Feature whose element type is being changed (only while mode === "retype").
  const [retypeEditor, setRetypeEditor] = useState<{ layer: FeatureLayer } | null>(null);
  // Group retype ("Zaznacz obszarem", only in "retype" mode): armed rectangle-selection
  // + the resulting flow state. The halo layer group holds the amber selection outlines.
  const [groupSelect, setGroupSelect] = useState(false);
  const groupSelectRef = useRef(false);
  const [groupRetype, setGroupRetype] = useState<GroupRetypeState | null>(null);
  const selectionHaloRef = useRef<L.LayerGroup | null>(null);
  const onSelectionRectRef = useRef<(b: L.LatLngBounds) => void>(() => {});
  const clearGroupRetype = useCallback(() => {
    selectionHaloRef.current?.clearLayers();
    setGroupRetype(null);
  }, []);

  // Annotation editor state — either "new" (pending placement at latlng) or "edit" (existing).
  const [annotationEditor, setAnnotationEditor] = useState<
    | { kind: "new"; latlng: L.LatLng; text: string; saving: boolean }
    | { kind: "edit"; annotation: ZEMapAnnotation; text: string }
    | null
  >(null);

  const svRef = useRef<{
    anchor: L.LatLng;
    marker: L.CircleMarker;
    line: L.Polyline | null;
  } | null>(null);

  // ── "Znajdź na mapie" tool (left-aligned in the toolbar row) ─────────────────
  // Collapsible search → locate by parcel TERYT (ULDK) OR by address (GUGiK geocoder),
  // depending on the selected mode. The result layer is purely visual (NOT part of the
  // saved drawing): replaced on each search, hidden when the panel collapses, restored
  // when it reopens, and discarded when the map unmounts (variant switch).
  const [locateOpen, setLocateOpen] = useState(false);
  const [locateMode, setLocateMode] = useState<"parcel" | "address">("parcel");
  const [locateQuery, setLocateQuery] = useState("");
  const [locateLoading, setLocateLoading] = useState(false);
  const [locateError, setLocateError] = useState<string | null>(null);
  const locateLayerRef = useRef<L.Layer | null>(null);
  // Last successful hit — kept so reopening the panel restores it without re-zooming.
  const lastLocateRef = useRef<LocateResult | null>(null);

  // (Re)bind permanent tooltips on drawn features. A line shows its custom label (when
  // set) stacked ABOVE its live length (when lengths are toggled on) at the line centre;
  // an object shows just its custom label above the marker.
  const refreshLabels = useCallback((showLengths: boolean) => {
    drawnGroupRef.current?.getLayers().forEach((layer) => {
      try {
      const label = layerLabel(layer);
      const labelHtml = label
        ? `<span class="ze-label-text">${escapeHtml(label).replace(/\n/g, "<br>")}</span>`
        : "";
      layer.unbindTooltip();
      if (layer instanceof L.Polyline) {
        // A feature with no vertices cannot be labelled: binding a permanent tooltip makes
        // Leaflet call getCenter(), which throws on an empty ring and used to take the whole
        // route down with it. Still reachable for drawings saved before `dropEmptyFeatures`.
        if (!hasVertices(layer)) return;
        const lengthHtml = showLengths
          ? `<span class="ze-label-length">${fmtMeters(layerLengthM(layer))}</span>`
          : "";
        const html = labelHtml + lengthHtml;
        if (!html) return;
        layer.bindTooltip(html, {
          permanent: true,
          direction: "center",
          className: "ze-line-label",
          interactive: false,
        });
      } else if (layer instanceof L.Marker && labelHtml) {
        layer.bindTooltip(labelHtml, {
          permanent: true,
          direction: "top",
          offset: L.point(0, -10),
          className: "ze-line-label ze-object-label",
          interactive: false,
        });
      }
      } catch (err) {
        // Labelling is decoration. Whatever one feature does, the other labels — and the
        // route itself — must still come up.
        console.error("ZEDrawingMap: nie udało się opisać elementu rysunku", err, layer);
      }
    });
  }, []);

  const iconForType = (t: ZEElementType | undefined) =>
    L.divIcon({
      html: objectSvg(t, 18),
      className: "ze-marker-icon",
      iconSize: [18, 18],
      iconAnchor: [9, 9],
    });

  const styleLayer = (layer: L.Layer, t: ZEElementType | undefined) => {
    if (layer instanceof L.Marker) layer.setIcon(iconForType(t));
    else if (layer instanceof L.Path)
      // Polygon extends Polyline, so the area check must come first — otherwise an
      // obszar gets a line style and renders as an unfilled outline.
      (layer as L.Path).setStyle(
        t?.kind === "area" ? areaStyleFor(t) : lineStyleFor(t),
      );
  };

  // Build the current FeatureCollection from the drawn layers and lift it up.
  const emit = (dirty: boolean) => {
    const group = drawnGroupRef.current;
    if (!group) return;
    // Safety net for coordinate-less features, whatever produced them: they are never
    // something a user drew (a real shape always has vertices), they render nothing, and
    // they used to break the editor on the next load. Filtering here means no path can
    // put one back on the server. See `dropEmptyFeatures`.
    const fc = dropEmptyFeatures(
      group.toGeoJSON() as unknown as ZEDrawingFeatureCollection,
    );
    onChangeRef.current(fc, dirty);
    refreshLabels(showLineLengthsRef.current);
  };

  // Per-layer change listeners so vertex edits / drags update the live summary.
  const attachLayer = (layer: L.Layer) => {
    layer.on("pm:edit", () => emit(true));
    layer.on("pm:dragend", () => emit(true));
    layer.on("pm:markerdragend", () => emit(true));
  };

  // ── map init (once) ──────────────────────────────────────────────────────
  useEffect(() => {
    if (mapRef.current || !containerRef.current) return;
    // NO preferCanvas: the drawn route must use the default SVG renderer, otherwise
    // Leaflet-Geoman's edit / drag / remove handles don't engage on it (canvas-rendered
    // vector layers can't be dragged and their vertex handles are unreliable). Only the
    // parcels overlay opts into a canvas renderer below.
    const map = L.map(containerRef.current, {
      center: [52.0, 19.0],
      zoom: 6,
      attributionControl: false,
    });
    osmRef.current = L.tileLayer(
      "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
      { maxZoom: 21 },
    ).addTo(map);
    // Parcels live in their own pane so we can switch OFF their pointer-events while a
    // drawing tool is active — otherwise the parcel popup eats the click instead of placing
    // a vertex. zIndex 350 = above OSM tiles (200), below the drawn route (overlayPane 400).
    // They render onto a dedicated canvas renderer so a dense overlay stays cheap; only the
    // drawn route needs SVG (parcels are never Geoman-edited — pmIgnore below).
    const parcelsPane = map.createPane("zeParcels");
    parcelsPane.style.zIndex = "350";
    parcelsRendererRef.current = L.canvas({ pane: "zeParcels", padding: 0.5 });
    parcelsRef.current = L.layerGroup().addTo(map);
    drawnGroupRef.current = L.featureGroup().addTo(map);
    // Amber outlines of the current group-retype selection — purely visual, never saved.
    selectionHaloRef.current = L.layerGroup().addTo(map);
    // Annotation pins live above the drawn route (overlayPane = 400) so they are
    // always clickable; own pane at 410.
    const annPane = map.createPane("zeAnnotations");
    annPane.style.zIndex = "410";
    annotationsGroupRef.current = L.layerGroup().addTo(map);
    // GUGiK reference overlays (działki granice+numery / GESUT) in their own panes — baseZ 300
    // keeps them above OSM (200) but below the parcels pane (350) and the drawn route (400).
    const g = createGugikLayers(map);
    gugikParcelsRef.current = g.parcels;
    gugikInfraRef.current = g.infra;
    gugikOrthoRef.current = g.ortho;
    gugikMpzpRef.current = g.mpzp;
    gugikGdosRef.current = g.gdos;
    gugikKonserwatorRef.current = g.konserwator;
    gugikKonserwatorEwidencjaRef.current = g.konserwatorEwidencja;
    gugikClosedAreasRef.current = g.closedAreas;
    const updateScale = () => {
      const lat = map.getCenter().lat;
      const z = map.getZoom();
      // Ground resolution: meters per CSS pixel at this zoom + latitude (Web Mercator).
      const groundRes = (2 * Math.PI * 6378137 * Math.cos((lat * Math.PI) / 180)) / (256 * Math.pow(2, z));
      // Physical screen pixel size assuming 96 DPI monitor (standard web assumption).
      const screenPixelM = 0.0254 / 96;
      const ratio = Math.round(groundRes / screenPixelM);
      setScaleRatio(`1 : ${ratio.toLocaleString("pl-PL")}`);
    };
    map.on("zoomend moveend", updateScale);
    updateScale();
    map.on("zoomend", () => setZoom(map.getZoom()));
    mapRef.current = map;

    // Geoman ships a built-in Polish locale ("pl") — translates all drawing/edit
    // tooltips ("Click to place first vertex" → "Kliknij, aby umieścić…", etc.).
    map.pm.setLang("pl");
    map.pm.setGlobalOptions({
      snappable: true,
      snapDistance: 20,
      continueDrawing: true,
    });

    // New feature drawn → tag it with the active element type, style + track it.
    let workingLayer: L.Polyline | null = null;
    map.on("pm:create", (e: { layer: L.Layer }) => {
      workingLayer = null;
      setDrawingLengthM(null);
      // Group-select rectangle ("Zaznacz obszarem"): it is a SELECTION box, not a drawn
      // element — consume it here instead of adding it to the drawing.
      if (groupSelectRef.current) {
        const b = (e.layer as L.Rectangle).getBounds?.();
        if (map.hasLayer(e.layer)) map.removeLayer(e.layer);
        if (b) onSelectionRectRef.current(b);
        return;
      }
      const t = activeTypeRef.current;
      const layer = e.layer as L.Layer & { feature?: GeoJSON.Feature };
      layer.feature =
        layer.feature ||
        ({ type: "Feature", properties: {} } as GeoJSON.Feature);
      layer.feature.properties = { element_type: t?.code ?? "" };
      styleLayer(layer, t ?? undefined);
      drawnGroupRef.current?.addLayer(layer);
      attachLayer(layer);
      emit(true);
    });
    map.on("pm:remove", (e: { layer?: L.Layer }) => {
      detachRemovedLayer(drawnGroupRef.current, e?.layer);
      emit(true);
    });

    // "Annotate" mode: map click places a new text pin (no Geoman tools involved).
    map.on("click", (e: L.LeafletMouseEvent) => {
      if (modeRef.current !== "annotate") return;
      setAnnotationEditor({ kind: "new", latlng: e.latlng, text: "", saving: false });
    });

    map.on("pm:drawstart", (e: { workingLayer: L.Layer; shape: string }) => {
      // The selection rectangle is a Polygon (extends Polyline) — don't show its "length".
      workingLayer =
        !groupSelectRef.current && e.workingLayer instanceof L.Polyline
          ? e.workingLayer
          : null;
    });
    map.on("pm:drawend", () => {
      workingLayer = null;
      setDrawingLengthM(null);
      // Draw ended for ANY reason (finish, Escape, tool switch): disarm the selection
      // rectangle. Without this an Escape-cancelled selection left `groupSelect` stuck
      // on — the rectangle tool was gone, so "Typ" looked completely dead.
      // (setState is stable; a no-op when already false, so normal draws are unaffected.)
      setGroupSelect(false);
    });
    map.on("mousemove", (e: L.LeafletMouseEvent) => {
      if (!workingLayer) return;
      const raw = workingLayer.getLatLngs();
      const lls: L.LatLng[] = Array.isArray(raw[0])
        ? (raw as L.LatLng[][]).flat()
        : (raw as L.LatLng[]);
      if (!lls.length) { setDrawingLengthM(null); return; }
      let total = 0;
      for (let i = 1; i < lls.length; i++) total += lls[i - 1].distanceTo(lls[i]);
      total += lls[lls.length - 1].distanceTo(e.latlng);
      setDrawingLengthM(total);
    });

    return () => {
      if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
      map.remove();
      mapRef.current = null;
      autoFittedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── arm the right draw tool for the selected element type ────────────────
  useEffect(() => {
    activeTypeRef.current = activeType;
    const map = mapRef.current;
    if (!map) return;
    map.pm.disableDraw();
    if (mode !== "draw") return;
    if (!activeType) return;
    if (activeType.kind === "line") {
      map.pm.enableDraw("Line", {
        hintlineStyle: { color: colorForType(activeType), dashArray: "4,4" },
      });
    } else if (activeType.kind === "area") {
      map.pm.enableDraw("Polygon", {
        hintlineStyle: { color: colorForType(activeType), dashArray: "4,4" },
        templineStyle: { color: colorForType(activeType) },
        pathOptions: areaStyleFor(activeType),
      });
    } else {
      map.pm.enableDraw("Marker", {
        markerStyle: { icon: iconForType(activeType) },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeType, mode]);

  // ── group-select rectangle (only while "Typ" is armed with "Zaznacz obszarem") ────
  // Declared AFTER the draw-tool effect on purpose: on a shared mode change both run,
  // and this one must win so the rectangle tool ends up enabled.
  useEffect(() => {
    const armed = mode === "retype" && groupSelect;
    groupSelectRef.current = armed;
    const map = mapRef.current;
    if (!map || !armed) return;
    map.pm.enableDraw("Rectangle", {
      hintlineStyle: { color: "#f59e0b", dashArray: "4,4" },
      pathOptions: { color: "#f59e0b", weight: 2, dashArray: "6 4", fillColor: "#f59e0b", fillOpacity: 0.08 },
      continueDrawing: false,
      // A selection box must land exactly where the user aims — snapping it to the
      // drawn features (global snappable: true) would warp the rectangle corners.
      snappable: false,
    });
    return () => {
      groupSelectRef.current = false;
      map.pm.disableDraw();
    };
  }, [mode, groupSelect]);

  // ── edit / drag / remove: driven PER-LAYER over the layers we own ─────────
  // We deliberately do NOT use Geoman's GLOBAL modes (enableGlobalEditMode/DragMode/
  // RemovalMode). Global modes scan every Geoman layer on the map — including the parcels
  // overlay that is cleared and rebuilt on each refresh — which made them flaky (stale-layer
  // throws) and, with the old canvas renderer, never actually engaged the route. Instead we
  // act directly on the drawn route group's layers: deterministic, only ever the route.
  useEffect(() => {
    const group = drawnGroupRef.current;
    const map = mapRef.current;
    if (!group || !map) return;

    const layers = group.getLayers();

    const removeOnClick = (e: L.LeafletMouseEvent) => {
      const layer = e.target as L.Layer;
      group.removeLayer(layer);
      if (map.hasLayer(layer)) map.removeLayer(layer);
      emit(true);
    };

    const labelOnClick = (e: L.LeafletMouseEvent) => {
      const layer = e.target as FeatureLayer;
      setLabelEditor({ layer, text: layerLabel(layer) });
    };

    const paramsOnClick = (e: L.LeafletMouseEvent) => {
      setParamsEditor({ layer: e.target as FeatureLayer });
    };

    const retypeOnClick = (e: L.LeafletMouseEvent) => {
      // Element click always wins: single-pick and area-pick coexist inside "Typ" —
      // if the selection rectangle is armed, disarm it and open the single editor.
      if (groupSelectRef.current) setGroupSelect(false);
      clearGroupRetype();
      setRetypeEditor({ layer: e.target as FeatureLayer });
    };

    // Geoman's per-layer controller (typed loosely — the lib's PMLayer type omits the drag mixin).
    const pmOf = (layer: L.Layer) =>
      (
        layer as unknown as {
          pm?: {
            enable?: (opts?: Record<string, unknown>) => void;
            disable?: () => void;
            enableLayerDrag?: () => void;
            disableLayerDrag?: () => void;
          };
        }
      ).pm;

    const reset = (layer: L.Layer) => {
      const pm = pmOf(layer);
      try {
        pm?.disable?.();
      } catch {
        /* layer may be detached mid-refresh */
      }
      try {
        pm?.disableLayerDrag?.();
      } catch {
        /* layer may be detached mid-refresh */
      }
      layer.off("click", removeOnClick);
      layer.off("click", labelOnClick);
      layer.off("click", paramsOnClick);
      layer.off("click", retypeOnClick);
    };

    for (const layer of layers) {
      reset(layer);
      try {
        const pm = pmOf(layer);
        if (mode === "edit")
          pm?.enable?.({
            allowSelfIntersection: true,
            snappable: true,
            snapDistance: 20,
          });
        else if (mode === "drag") pm?.enableLayerDrag?.();
        else if (mode === "remove") layer.on("click", removeOnClick);
        else if (mode === "label") layer.on("click", labelOnClick);
        else if (mode === "params") layer.on("click", paramsOnClick);
        else if (mode === "retype") layer.on("click", retypeOnClick);
      } catch (err) {
        console.error("Geoman per-layer mode failed", err);
      }
    }

    return () => {
      for (const layer of layers) reset(layer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  // Keep modeRef in sync so the map click handler (registered once at init) can read it.
  useEffect(() => {
    modeRef.current = mode;
    // Disable Geoman drawing when switching to "annotate" mode so clicks go to our handler.
    if (mode === "annotate") mapRef.current?.pm.disableDraw();
    // Close annotation editor when leaving annotate mode.
    if (mode !== "annotate") setAnnotationEditor(null);
  }, [mode]);

  // External panel asked us to edit a specific annotation: fly to it + open editor.
  useEffect(() => {
    if (!externalAnnotationEdit) return;
    const marker = annotationMarkersRef.current.get(externalAnnotationEdit.id);
    if (marker && mapRef.current) {
      const ll = marker.getLatLng();
      mapRef.current.setView(ll, Math.max(mapRef.current.getZoom(), 16), { animate: true });
    }
    setAnnotationEditor({ kind: "edit", annotation: externalAnnotationEdit, text: externalAnnotationEdit.text });
    onExternalAnnotationEditHandled?.();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalAnnotationEdit]);

  // Parcels must not steal clicks while drawing/editing tools are active. Interactive
  // in "off" mode (popup) and "info" mode (callback to parent).
  useEffect(() => {
    const pane = mapRef.current?.getPane("zeParcels");
    if (pane)
      pane.style.pointerEvents =
        mode === "off" || mode === "info" ? "auto" : "none";
  }, [mode]);

  // ── Street View tool: two-phase click (position → direction → open URL) ─────
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    if (mode !== "streetview") {
      if (svRef.current) {
        map.removeLayer(svRef.current.marker);
        if (svRef.current.line) map.removeLayer(svRef.current.line);
        svRef.current = null;
      }
      map.getContainer().style.cursor = "";
      return;
    }

    map.getContainer().style.cursor = "crosshair";

    const onClick = (e: L.LeafletMouseEvent) => {
      if (!svRef.current) {
        const marker = L.circleMarker(e.latlng, {
          radius: 8,
          color: "#fff",
          weight: 2,
          fillColor: "#ef4444",
          fillOpacity: 1,
        }).addTo(map);
        svRef.current = { anchor: e.latlng, marker, line: null };
      } else {
        const { anchor, marker, line } = svRef.current;
        const heading = Math.round(headingBetween(anchor, e.latlng));
        window.open(
          `https://maps.google.com/maps?layer=c&cbll=${anchor.lat},${anchor.lng}&cbp=11,${heading},0,0,0`,
          "_blank",
          "noopener,noreferrer",
        );
        map.removeLayer(marker);
        if (line) map.removeLayer(line);
        svRef.current = null;
        setMode("off");
      }
    };

    const onMouseMove = (e: L.LeafletMouseEvent) => {
      if (!svRef.current) return;
      if (svRef.current.line) {
        svRef.current.line.setLatLngs([svRef.current.anchor, e.latlng]);
      } else {
        svRef.current.line = L.polyline([svRef.current.anchor, e.latlng], {
          color: "#3b82f6",
          weight: 2,
          dashArray: "6,4",
        }).addTo(map);
      }
    };

    map.on("click", onClick);
    map.on("mousemove", onMouseMove);
    return () => {
      map.off("click", onClick);
      map.off("mousemove", onMouseMove);
    };
  }, [mode]);

  // "Info" mode + a NID overlay on: a map click pulls the monument(s) under the cursor from
  // zabytek.gov.pl (WMS GetFeatureInfo) into a popup. Parcel clicks still reach the parent
  // callback — Leaflet bubbles the click to the map, so both fire.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || mode !== "info") return;
    let reqId = 0;
    let cancelled = false;
    let openPopup: L.Popup | null = null;
    const onClick = async (e: L.LeafletMouseEvent) => {
      const sources = {
        ewidencja: !!gugikStateRef.current.konserwatorEwidencja,
        rejestr: !!gugikStateRef.current.konserwator,
      };
      if (!sources.ewidencja && !sources.rejestr) return;
      const myReq = ++reqId;
      const popup = L.popup({ maxWidth: 340, minWidth: 210 })
        .setLatLng(e.latlng)
        .setContent(
          `<div style="font-family:ui-monospace,monospace;font-size:12px;color:#555;padding:2px 0">Pobieranie z zabytek.gov.pl…</div>`,
        )
        .openOn(map);
      openPopup = popup;
      const monuments = await fetchNidMonuments(map, e.latlng, sources);
      if (cancelled || myReq !== reqId || !mapRef.current) return;
      popup.setContent(
        `<div style="min-width:210px;max-width:320px;font-family:ui-monospace,monospace;line-height:1.4">${nidMonumentsHtml(monuments)}</div>`,
      );
    };
    map.on("click", onClick);
    return () => {
      cancelled = true;
      map.off("click", onClick);
      if (openPopup) { try { map.closePopup(openPopup); } catch { /* detached */ } }
    };
  }, [mode]);

  // Adds a dashed highlight polygon then removes it after a delay.
  // Reads refs at call time — no stale closure risk.
  const showParcelHighlight = useCallback((geom: unknown) => {
    const map = mapRef.current;
    if (!map || !geom) return;
    // Clear any existing highlight layer
    if (highlightLayerRef.current) {
      try { map.removeLayer(highlightLayerRef.current); } catch { /* detached */ }
      highlightLayerRef.current = null;
    }
    const hl = safeGeoJson(geom, {
      style: { color: "#dc2626", weight: 2.5, opacity: 0.7, fillOpacity: 0.08,
               dashArray: "8 5", lineCap: "square" as const, lineJoin: "miter" as const },
    }, "podświetlenie działki");
    if (!hl) return;
    hl.addTo(map);
    highlightLayerRef.current = hl;
    highlightTimerRef.current = setTimeout(() => {
      try { mapRef.current?.removeLayer(hl); } catch { /* ok */ }
      if (highlightLayerRef.current === hl) highlightLayerRef.current = null;
      highlightTimerRef.current = null;
    }, 3500);
  }, []);

  // Expose fitToParcel imperatively — no React state/effect cycle, timer resets
  // immediately on every call without any batching or stale-closure risk.
  useImperativeHandle(ref, () => ({
    fitToParcel: (teryt: string) => {
      const map = mapRef.current;
      if (!map) return;

      // Cancel any running highlight timer immediately
      if (highlightTimerRef.current) {
        clearTimeout(highlightTimerRef.current);
        highlightTimerRef.current = null;
      }

      const fitAndHighlight = (geom: unknown) => {
        const b = safeGeoJson(geom, {}, "dopasowanie do działki")?.getBounds();
        if (b?.isValid()) { map.fitBounds(b, { padding: [40, 40] }); showParcelHighlight(geom); }
      };

      // 1. Geometry from parcels prop (fast, local)
      const parcelGeom = parcels.find((p) => p.teryt === teryt)?.geometry;
      if (parcelGeom) { fitAndHighlight(parcelGeom); return; }

      // 2. Cached bounds (no geometry) — fit only
      const cached = parcelBoundsRef.current.get(teryt);
      if (cached?.isValid()) { map.fitBounds(cached, { padding: [40, 40] }); return; }

      // 3. Fetch from ULDK (obreb or parcel without stored geometry)
      getUldkParcelGeom(teryt).then((res) => {
        if (!res?.geometry || !mapRef.current) return;
        const b = safeGeoJson(res.geometry, {}, `działka ULDK ${teryt}`)?.getBounds();
        if (b?.isValid()) parcelBoundsRef.current.set(teryt, b);
        fitAndHighlight(res.geometry);
      }).catch(() => {});
    },
    getCenter: () => {
      const c = mapRef.current?.getCenter();
      return c ? { lat: c.lat, lng: c.lng } : null;
    },
    flyToAnnotation: (id: string) => {
      const marker = annotationMarkersRef.current.get(id);
      if (!marker || !mapRef.current) return;
      const ll = marker.getLatLng();
      mapRef.current.setView(ll, Math.max(mapRef.current.getZoom(), 16), { animate: true });
    },
  }), [parcels, showParcelHighlight]);

  // GUGiK reference overlays — toggled from the on-map panel.
  useGugikToggle(mapRef, gugikParcelsRef, gugik.parcels);
  useGugikToggle(mapRef, gugikInfraRef, gugik.infra);
  useGugikToggle(mapRef, gugikOrthoRef, gugik.ortho);
  useGugikToggle(mapRef, gugikMpzpRef, gugik.mpzp);
  useGugikToggle(mapRef, gugikGdosRef, gugik.gdos);
  useGugikToggle(mapRef, gugikKonserwatorRef, !!gugik.konserwator);
  useGugikToggle(mapRef, gugikKonserwatorEwidencjaRef, !!gugik.konserwatorEwidencja);
  useGugikToggle(mapRef, gugikClosedAreasRef, !!gugik.closedAreas);
  const rejestrEmpty = useNidTileContent(gugikKonserwatorRef, !!gugik.konserwator);
  const ewidencjaEmpty = useNidTileContent(gugikKonserwatorEwidencjaRef, !!gugik.konserwatorEwidencja);
  const gugikLoading = useGugikLoading(
    gugikParcelsRef,
    gugikInfraRef,
    gugikOrthoRef,
    gugik,
    gugikMpzpRef,
    gugikGdosRef,
    gugikKonserwatorRef,
    gugikKonserwatorEwidencjaRef,
    undefined,
    gugikClosedAreasRef,
  );

  // ── load the variant's existing drawing for editing (once per map instance) ─────
  useEffect(() => {
    const group = drawnGroupRef.current;
    if (!group || !initialDrawing || loadedIntoRef.current === group) return;
    loadedIntoRef.current = group;
    const loaded = safeGeoJson(
      initialDrawing,
      {
        style: (feat) => {
          const t = typesByCodeRef.current[feat?.properties?.element_type];
          return t?.kind === "area" ? areaStyleFor(t) : lineStyleFor(t);
        },
        pointToLayer: (feat, latlng) =>
          L.marker(latlng, {
            icon: iconForType(
              typesByCodeRef.current[feat?.properties?.element_type],
            ),
          }),
        onEachFeature: (feat, layer) => {
          (layer as L.Layer & { feature?: GeoJSON.Feature }).feature = feat;
          group.addLayer(layer);
          attachLayer(layer);
        },
      },
      "rysunek wariantu",
    );
    // A drawing that failed to parse may have loaded only its first few features before
    // Leaflet threw. Deliberately skip `emit` in that case: the workspace keeps `currentFc`
    // null, which is what disables Zapisz — otherwise the user would save the truncated
    // route over the intact one on the server.
    if (!loaded) {
      setDrawingLoadFailed(true);
      return;
    }
    setDrawingLoadFailed(false);
    setEmptyFeatureCount(countEmptyFeatures(initialDrawing));
    emit(false);
    // Only block the parcel auto-fit when there is an actual drawn route — an empty
    // FeatureCollection must not set the flag prematurely, or parcels arriving later
    // will never trigger the initial fit (cold page-load race).
    const hasFeatures = (initialDrawing.features?.length ?? 0) > 0;
    if (hasFeatures) {
      fitToDrawing();
      autoFittedRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialDrawing]);

  // ── parcels overlay (recompute result) ───────────────────────────────────
  useEffect(() => {
    const group = parcelsRef.current;
    if (!group) return;
    group.clearLayers();
    parcelBoundsRef.current.clear();
    const draftByTeryt = new Map((drafts ?? []).map((d) => [d.teryt, d]));
    for (const p of parcels) {
      if (!p.geometry) continue;
      const draft = draftByTeryt.get(p.teryt);
      const fillColor = ownershipCodeColor(
        (draft?.ownership_override || p.ownership_code),
      );
      const layer = safeGeoJson(
        p.geometry,
        {
          pane: "zeParcels",
          renderer: parcelsRendererRef.current ?? undefined,
          pmIgnore: true,
          style: { color: "#555", weight: 1, fillColor, fillOpacity: 0.38 },
        } as L.GeoJSONOptions,
        `działka ${p.teryt}`,
      );
      if (!layer) continue;
      layer.bindPopup(buildParcelPopupHtml(p, draft), { maxWidth: 320 });
      // Store bounds for fit-to-parcel.
      const b = layer.getBounds();
      if (b.isValid()) parcelBoundsRef.current.set(p.teryt, b);
      // In "info" mode, clicking a parcel fires the callback to the parent.
      layer.on("click", () => onParcelClickRef.current?.(p.teryt));
      layer.addTo(group);
    }
    // Auto-fit when parcels first load and no route drawing has fitted yet (e.g. tender
    // with only a parcel/obreb and no drawn trace).
    if (!autoFittedRef.current && parcels.length > 0) {
      autoFittedRef.current = true;
      fitToDrawing();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parcels, drafts]);

  // ── MPZP gap overlay — red+black corridor where route lacks MPZP ─────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (mpzpGapLayerRef.current) {
      if (map.hasLayer(mpzpGapLayerRef.current)) map.removeLayer(mpzpGapLayerRef.current);
      mpzpGapLayerRef.current = null;
    }
    if (!mpzpGapGeom) return;
    // Dedicate a pane above parcels (350) so the gap is always visible.
    if (!map.getPane("mpzpGap")) {
      map.createPane("mpzpGap").style.zIndex = "360";
    }
    const layer = safeGeoJson(
      mpzpGapGeom,
      {
        pane: "mpzpGap",
        style: { color: "#000", weight: 1, dashArray: "4 2", fillColor: "#dc2626", fillOpacity: 0.65 },
      },
      "braki MPZP",
    );
    if (!layer) return;
    layer.addTo(map);
    mpzpGapLayerRef.current = layer;
  }, [mpzpGapGeom]);

  // ── MPZP "nie wiadomo" overlay — grey corridor where coverage can't be determined ──
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (mpzpUnknownLayerRef.current) {
      if (map.hasLayer(mpzpUnknownLayerRef.current)) map.removeLayer(mpzpUnknownLayerRef.current);
      mpzpUnknownLayerRef.current = null;
    }
    if (!mpzpUnknownGeom) return;
    // Below the red gap pane (360) so confirmed gaps always win when the two overlap.
    if (!map.getPane("mpzpUnknown")) {
      map.createPane("mpzpUnknown").style.zIndex = "355";
    }
    const layer = safeGeoJson(
      mpzpUnknownGeom,
      {
        pane: "mpzpUnknown",
        style: { color: "#374151", weight: 1, dashArray: "4 2", fillColor: "#9ca3af", fillOpacity: 0.5 },
      },
      "MPZP nie wiadomo",
    );
    if (!layer) return;
    layer.addTo(map);
    mpzpUnknownLayerRef.current = layer;
  }, [mpzpUnknownGeom]);

  // ── Protected areas (GDOŚ) overlay — route∩area corridors colored per form type ──
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (protectedAreasLayerRef.current) {
      if (map.hasLayer(protectedAreasLayerRef.current)) map.removeLayer(protectedAreasLayerRef.current);
      protectedAreasLayerRef.current = null;
    }
    if (!protectedAreasGeom) return;
    // Between the grey "nie wiadomo" (355) and the red MPZP gap (360) — a confirmed
    // MPZP gap stays visible on top when the corridors overlap.
    if (!map.getPane("protectedAreas")) {
      map.createPane("protectedAreas").style.zIndex = "358";
    }
    const layer = safeGeoJson(
      protectedAreasGeom,
      {
        pane: "protectedAreas",
        style: (feat) => ({
          color: "#000",
          weight: 1,
          dashArray: "4 2",
          fillColor: protectedAreaColor(feat?.properties?.layer_key ?? ""),
          fillOpacity: 0.55,
        }),
        onEachFeature: (feat, lyr) => {
          const p = feat?.properties ?? {};
          const name = p.nazwa ? ` «${p.nazwa}»` : "";
          lyr.bindTooltip(`${p.type_label ?? "Obszar chroniony"}${name}`, { sticky: true });
        },
      },
      "obszary chronione (GDOŚ)",
    );
    if (!layer) return;
    layer.addTo(map);
    protectedAreasLayerRef.current = layer;
  }, [protectedAreasGeom]);

  // ── Przejścia przez wody (Hydroportal) — punkty nad wszystkimi korytarzami ──────
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (waterCrossingsLayerRef.current) {
      if (map.hasLayer(waterCrossingsLayerRef.current)) map.removeLayer(waterCrossingsLayerRef.current);
      waterCrossingsLayerRef.current = null;
    }
    if (!waterCrossingsGeom) return;
    // Nad korytarzami MPZP/GDOŚ (355–360): punkt jest mały, więc nie może chować się
    // pod półprzezroczystym wypełnieniem.
    if (!map.getPane("waterCrossings")) {
      map.createPane("waterCrossings").style.zIndex = "365";
    }
    const layer = safeGeoJson(
      waterCrossingsGeom,
      {
        pane: "waterCrossings",
        pointToLayer: (feat, latlng) =>
          L.circleMarker(latlng, {
            pane: "waterCrossings",
            radius: 6,
            color: "#000",
            weight: 1,
            fillColor:
              feat?.properties?.key === "przejscie_wp_napowietrzne" ? "#0ea5e9" : "#1d4ed8",
            fillOpacity: 0.9,
          }),
        onEachFeature: (feat, lyr) => {
          const p = feat?.properties ?? {};
          const river = p.feature_name ? ` «${p.feature_name}»` : "";
          const element = p.element_label ? ` — ${p.element_label}` : "";
          const review = p.needs_review ? " · SPRAWDŹ RODZAJ" : "";
          lyr.bindTooltip(`${p.kind_label ?? "Przejście przez wody"}${river}${element}${review}`, {
            sticky: true,
          });
        },
      },
      "przejścia przez wody",
    );
    if (!layer) return;
    layer.addTo(map);
    waterCrossingsLayerRef.current = layer;
  }, [waterCrossingsGeom]);

  const fitToDrawing = () => {
    const map = mapRef.current;
    if (!map) return;
    const parts: L.LatLngBounds[] = [];
    const dg = drawnGroupRef.current;
    if (dg && dg.getLayers().length) {
      const b = dg.getBounds();
      if (b.isValid()) parts.push(b);
    }
    const pg = parcelsRef.current;
    pg?.eachLayer((l) => {
      const b = (l as L.GeoJSON).getBounds?.();
      if (b?.isValid()) parts.push(b);
    });
    if (!parts.length) return;
    const bounds = parts.slice(1).reduce((acc, b) => acc.extend(b), parts[0]);
    map.fitBounds(bounds, { padding: [30, 30] });
  };

  useEffect(() => {
    if (fitTrigger) fitToDrawing();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fitTrigger]);

  // ── annotation pin rendering ─────────────────────────────────────────────────
  // Full-replace the annotation layer whenever the `annotations` prop changes.
  useEffect(() => {
    const group = annotationsGroupRef.current;
    if (!group) return;
    group.clearLayers();
    annotationMarkersRef.current.clear();

    for (const ann of annotations) {
      const geo = ann.geometry;
      if (!geo || geo.type !== "Point") continue;
      const coords = geo.coordinates as [number, number];
      // L.latLng throws on a non-finite pair ("Invalid LatLng object"), and this runs in an
      // effect — an annotation saved with a broken point would take the whole route down.
      if (!Number.isFinite(coords?.[0]) || !Number.isFinite(coords?.[1])) {
        console.error("ZEDrawingMap: pominięto adnotację o nieprawidłowych współrzędnych", ann);
        continue;
      }
      const ll = L.latLng(coords[1], coords[0]);
      const marker = L.marker(ll, {
        icon: L.divIcon({
          html: `<div class="ze-ann-pin" title="${escapeHtml(ann.text.slice(0, 80))}">
            <div class="ze-ann-pin-dot"></div>
          </div>`,
          className: "",
          iconSize: [20, 28],
          iconAnchor: [10, 28],
        }),
        pane: "zeAnnotations",
      });
      // Show the first line as a permanent tooltip above the pin.
      const firstLine = ann.text.split("\n")[0].trim().slice(0, 60);
      if (firstLine) {
        marker.bindTooltip(escapeHtml(firstLine), {
          permanent: true,
          direction: "top",
          offset: L.point(0, -28),
          className: "ze-line-label ze-ann-label",
          interactive: false,
        });
      }
      marker.on("click", (e: L.LeafletMouseEvent) => {
        L.DomEvent.stopPropagation(e);
        setAnnotationEditor({ kind: "edit", annotation: ann, text: ann.text });
      });
      group.addLayer(marker);
      annotationMarkersRef.current.set(ann.id, marker);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [annotations]);

  useEffect(() => {
    refreshLabels(showLineLengths);
  }, [showLineLengths, refreshLabels]);

  // Leaving a tool closes its on-map editor.
  useEffect(() => {
    if (mode !== "label") setLabelEditor(null);
    if (mode !== "params") setParamsEditor(null);
    if (mode !== "retype") {
      setRetypeEditor(null);
      setGroupSelect(false);
      clearGroupRetype();
    }
  }, [mode, clearGroupRetype]);

  /** Write one parameter onto the clicked feature. `undefined` REMOVES the key, which is
   *  what hands the value back to the variant default — storing "" instead would pin an
   *  empty answer and quietly detach the segment from every later default change. */
  const commitParam = (spec: ZEParamSpec, raw: string) => {
    if (!paramsEditor) return;
    const layer = paramsEditor.layer;
    layer.feature =
      layer.feature || ({ type: "Feature", properties: {} } as GeoJSON.Feature);
    const props = (layer.feature.properties = layer.feature.properties || {});
    const attrs = { ...layerAttrs(layer) };
    const parsed = parseParamInput(spec, raw);
    if (parsed === undefined) delete attrs[spec.key];
    else attrs[spec.key] = parsed;
    if (Object.keys(attrs).length) props.attrs = attrs;
    else delete props.attrs;
    setParamsEditor({ layer });
    emit(true);
  };

  /** Change the clicked feature's element type. Same-kind only (the panel offers no
   *  other options) — geometry stays, so a line becomes another line type itd. Restyles
   *  the layer in place and lifts the drawing up as dirty. */
  const commitRetype = (t: ZEElementType) => {
    if (!retypeEditor) return;
    const layer = retypeEditor.layer;
    layer.feature =
      layer.feature || ({ type: "Feature", properties: {} } as GeoJSON.Feature);
    const props = (layer.feature.properties = layer.feature.properties || {});
    setRetypeEditor(null);
    if (props.element_type === t.code) return;
    props.element_type = t.code;
    styleLayer(layer, t);
    emit(true);
  };

  // Amber outline clone of a selected layer — visual only, cleared with the selection.
  const addSelectionHalo = (layer: L.Layer, halo: L.LayerGroup) => {
    if (layer instanceof L.Marker) {
      halo.addLayer(
        L.circleMarker(layer.getLatLng(), {
          radius: 14, color: "#f59e0b", weight: 3, fill: false, opacity: 0.9,
          interactive: false, pmIgnore: true,
        } as L.CircleMarkerOptions),
      );
    } else if (layer instanceof L.Polygon) {
      halo.addLayer(
        L.polygon(layer.getLatLngs() as L.LatLngExpression[], {
          color: "#f59e0b", weight: 4, opacity: 0.9, fill: false, dashArray: "6 4",
          interactive: false, pmIgnore: true,
        } as L.PolylineOptions),
      );
    } else if (layer instanceof L.Polyline) {
      halo.addLayer(
        L.polyline(layer.getLatLngs() as L.LatLngExpression[], {
          color: "#f59e0b", weight: 9, opacity: 0.45,
          interactive: false, pmIgnore: true,
        } as L.PolylineOptions),
      );
    }
  };

  // One type chosen (or the only one present) → halo its layers, ask for the new type.
  const selectGroupOfType = (code: string, layers: FeatureLayer[]) => {
    const halo = selectionHaloRef.current;
    halo?.clearLayers();
    if (halo) for (const l of layers) addSelectionHalo(l, halo);
    setGroupRetype({ step: "target", code, layers });
  };

  // Selection rectangle finished → bucket the hit elements by their current type.
  const handleSelectionRect = (bounds: L.LatLngBounds) => {
    setGroupSelect(false);
    setRetypeEditor(null);
    clearGroupRetype();
    const group = drawnGroupRef.current;
    if (!group) return;
    const byCode = new Map<string, FeatureLayer[]>();
    for (const layer of group.getLayers()) {
      if (!layerInBounds(layer, bounds)) continue;
      const code = layerTypeCode(layer);
      const arr = byCode.get(code);
      if (arr) arr.push(layer as FeatureLayer);
      else byCode.set(code, [layer as FeatureLayer]);
    }
    const entries = [...byCode.entries()];
    if (entries.length === 0) setGroupRetype({ step: "empty" });
    else if (entries.length === 1) selectGroupOfType(entries[0][0], entries[0][1]);
    else
      setGroupRetype({
        step: "pickType",
        groups: entries.map(([code, layers]) => ({ code, layers })),
      });
  };
  onSelectionRectRef.current = handleSelectionRect;

  /** Apply the chosen type to EVERY selected layer (all share one current type/kind). */
  const commitGroupRetype = (t: ZEElementType) => {
    if (groupRetype?.step !== "target") return;
    for (const layer of groupRetype.layers) {
      layer.feature =
        layer.feature || ({ type: "Feature", properties: {} } as GeoJSON.Feature);
      const props = (layer.feature.properties = layer.feature.properties || {});
      if (props.element_type === t.code) continue;
      props.element_type = t.code;
      styleLayer(layer, t);
    }
    clearGroupRetype();
    if (t.code !== groupRetype.code) emit(true);
  };

  // Write the edited label onto the feature ("" clears it) and lift the change up —
  // it is persisted together with the rest of the drawing on the page-level save.
  const commitLabel = (text: string) => {
    if (!labelEditor) return;
    const layer = labelEditor.layer;
    layer.feature =
      layer.feature || ({ type: "Feature", properties: {} } as GeoJSON.Feature);
    const props = (layer.feature.properties = layer.feature.properties || {});
    const cleaned = text.trim();
    if (cleaned) props.label = cleaned;
    else delete props.label;
    setLabelEditor(null);
    emit(true);
  };

  const clearDrawing = () => {
    if (!window.confirm("Wyczyścić cały rysunek tego wariantu?")) return;
    drawnGroupRef.current?.clearLayers();
    emit(true);
  };

  const commitAnnotation = async (text: string) => {
    const trimmed = text.trim();
    if (!trimmed || !annotationEditor) return;
    if (annotationEditor.kind === "new") {
      const { latlng } = annotationEditor;
      setAnnotationEditor((s) => s && s.kind === "new" ? { ...s, saving: true } : s);
      try {
        await onAnnotationCreateRef.current?.(latlng.lat, latlng.lng, trimmed);
      } finally {
        setAnnotationEditor(null);
      }
    } else {
      const { annotation } = annotationEditor;
      setAnnotationEditor(null);
      onAnnotationUpdateRef.current?.(annotation.id, { text: trimmed });
    }
  };

  const deleteAnnotation = (id: string) => {
    setAnnotationEditor(null);
    onAnnotationDeleteRef.current?.(id);
  };

  // Render (or replace) the locate result — NO fly/zoom. A point marker for an address,
  // a dashed outline for a parcel. Used by a fresh search and to restore on reopen.
  const renderLocateResult = useCallback((res: LocateResult) => {
    const map = mapRef.current;
    if (!map) return;
    if (locateLayerRef.current) {
      map.removeLayer(locateLayerRef.current);
      locateLayerRef.current = null;
    }
    let layer: L.Layer;
    if (res.kind === "address") {
      const marker = L.circleMarker([res.lat, res.lng], {
        radius: 8,
        color: "#fff",
        weight: 2,
        fillColor: "#2563eb",
        fillOpacity: 1,
      });
      marker.bindTooltip(escapeHtml(res.label), {
        permanent: true,
        direction: "top",
        offset: L.point(0, -8),
        className: "ze-line-label",
        interactive: false,
      });
      layer = marker;
    } else {
      const gj = L.geoJSON(res.geometry as unknown as GeoJSON.GeoJsonObject, {
        style: {
          color: "#2563eb",
          weight: 2.5,
          opacity: 0.9,
          fillColor: "#2563eb",
          fillOpacity: 0.1,
          dashArray: "6 4",
        },
      });
      gj.bindTooltip(escapeHtml(res.teryt), {
        permanent: true,
        direction: "center",
        className: "ze-line-label",
        interactive: false,
      });
      layer = gj;
    }
    layer.addTo(map);
    locateLayerRef.current = layer;
  }, []);

  const clearLocateLayer = useCallback(() => {
    const map = mapRef.current;
    if (locateLayerRef.current && map) map.removeLayer(locateLayerRef.current);
    locateLayerRef.current = null;
  }, []);

  // Locate the typed query by the selected mode — parcel TERYT (variant parcels → ULDK) or
  // address (GUGiK geocoder) — then fit/fly to it and drop the result layer. The hit is
  // remembered so reopening the panel restores it (see effect below); zoom only happens
  // here, on an explicit "Lokalizuj" click.
  const locate = async () => {
    const map = mapRef.current;
    const q = locateQuery.trim();
    if (!map || !q) return;
    setLocateLoading(true);
    setLocateError(null);
    try {
      if (locateMode === "address") {
        const { results } = await geocodeAddress(q);
        if (!results.length) {
          setLocateError("Nie znaleziono tego adresu.");
          return;
        }
        const best = results[0];
        const res: LocateResult = { kind: "address", lat: best.lat, lng: best.lng, label: best.label };
        lastLocateRef.current = res;
        renderLocateResult(res);
        map.flyTo([best.lat, best.lng], Math.max(map.getZoom(), 17), { animate: true });
      } else {
        // Parcel mode: try the variant's own parcels first, else fetch by id from ULDK.
        let geometry = parcels.find((p) => p.teryt === q)?.geometry ?? null;
        if (!geometry) {
          const res = await getUldkParcelGeom(q);
          geometry = res?.geometry ?? null;
        }
        if (!geometry) {
          setLocateError("Nie znaleziono działki o tym identyfikatorze.");
          return;
        }
        const res: LocateResult = { kind: "parcel", teryt: q, geometry };
        lastLocateRef.current = res;
        renderLocateResult(res);
        const b = L.geoJSON(geometry as unknown as GeoJSON.GeoJsonObject).getBounds();
        if (b.isValid()) map.fitBounds(b, { padding: [40, 40] });
      }
    } catch {
      setLocateError("Błąd lokalizowania — spróbuj ponownie.");
    } finally {
      setLocateLoading(false);
    }
  };

  // The result layer lives only while the panel is open: hide it on collapse, restore the
  // last hit on reopen — without re-centering (only "Lokalizuj" zooms the map).
  useEffect(() => {
    if (locateOpen) {
      if (lastLocateRef.current) renderLocateResult(lastLocateRef.current);
    } else {
      clearLocateLayer();
    }
  }, [locateOpen, renderLocateResult, clearLocateLayer]);

  // Open Geoportal on the CURRENT map view. The new viewer takes a centre + scale; the scale
  // is derived server-side from the extent's metric size (reprojected there) and THIS map's
  // pixel size, so the whole view fits — hence the backend hop before opening the link.
  const openGeoportal = async () => {
    const map = mapRef.current;
    if (!map) return;
    const b = map.getBounds();
    const sw = b.getSouthWest();
    const ne = b.getNorthEast();
    const size = map.getSize();
    try {
      const { url } = await getGeoportalUrl({
        sw_lng: sw.lng,
        sw_lat: sw.lat,
        ne_lng: ne.lng,
        ne_lat: ne.lat,
        width_px: Math.round(size.x),
        height_px: Math.round(size.y),
      });
      window.open(url, "_blank", "noopener,noreferrer");
    } catch {
      window.alert("Nie udało się otworzyć Geoportalu dla tego widoku.");
    }
  };

  // Keep Leaflet sized when fullscreen flips (the parent owns the fullscreen state): the
  // container's box changes, so recompute tiles/handles. A deferred pass covers the
  // browser settling the new layout.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const raf = requestAnimationFrame(() => map.invalidateSize());
    const t = setTimeout(() => map.invalidateSize(), 250);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(t);
    };
  }, [isFullscreen]);

  const modeBtn = (
    m: EditMode,
    icon: React.ReactNode,
    label: string,
    title: string,
  ) => (
    <button
      type="button"
      title={title}
      onClick={() => setMode((cur) => (cur === m ? "off" : m))}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 border-r border-[var(--color-border)] last:border-r-0 font-mono text-[11px] uppercase tracking-wider transition-colors ${
        mode === m
          ? "bg-[var(--color-primary)] text-[var(--color-text-inverse)]"
          : "text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-elevated)]"
      }`}
    >
      {icon}
      <span>{label}</span>
    </button>
  );

  const statusText = () => {
    if (mode === "draw")
      return activeType ? (
        <>
          Rysujesz:{" "}
          <span
            className="font-bold"
            style={{ color: colorForType(activeType) }}
          >
            {activeType.label}
          </span>
        </>
      ) : (
        <span className="text-amber-600">Wybierz typ elementu z palety →</span>
      );
    if (mode === "edit")
      return "Edycja: przeciągnij wierzchołki linii / punkty";
    if (mode === "drag") return "Przesuwanie: przeciągnij cały element";
    if (mode === "remove") return "Usuwanie: kliknij element, aby go usunąć";
    if (mode === "label")
      return "Etykieta: kliknij element, aby dodać lub edytować etykietę";
    if (mode === "params")
      return "Parametry: kliknij element, aby ustawić głębokość, grunt, średnicę…";
    if (mode === "retype")
      return groupSelect
        ? "Zaznaczanie obszarem: narysuj prostokąt (albo kliknij pojedynczy element)"
        : "Zmiana typu: kliknij element albo użyj „Zaznacz obszarem”, aby zmienić wiele naraz";
    if (mode === "annotate")
      return "Adnotacja: kliknij dowolne miejsce na mapie, aby dodać komentarz tekstowy";
    if (mode === "info")
      return gugik.konserwator || gugik.konserwatorEwidencja
        ? "Informacje o działce · klik pobiera też dane zabytku z zabytek.gov.pl"
        : "Informacje o działce";
    if (mode === "streetview")
      return svRef.current
        ? "Kliknij w kierunku, który chcesz zobaczyć"
        : "Kliknij punkt na mapie";
    return "Podgląd (rysowanie wyłączone)";
  };

  return (
    <div
      ref={wrapperRef}
      className={`isolate${isFullscreen ? " bg-white flex flex-col flex-1 min-h-0" : ""}`}
    >
      <style>{`
        .leaflet-tooltip.ze-line-label {
          background: rgba(255,255,255,0.93);
          border: 1px solid #6ee7b7;
          border-radius: 3px;
          color: #065f46;
          font-family: ui-monospace, monospace;
          font-size: 11px;
          font-weight: 700;
          padding: 2px 6px;
          pointer-events: none;
          white-space: nowrap;
          box-shadow: 0 1px 3px rgba(0,0,0,0.12);
        }
        .leaflet-tooltip.ze-line-label::before { display: none; }
        .leaflet-tooltip.ze-line-label .ze-label-text {
          display: block;
          text-align: center;
          color: #1f2937;
          font-weight: 600;
        }
        .leaflet-tooltip.ze-line-label .ze-label-length {
          display: block;
          text-align: center;
        }
        .leaflet-tooltip.ze-object-label {
          border-color: #9ca3af;
        }
        .leaflet-tooltip.ze-ann-label {
          border-color: #f59e0b;
          background: rgba(255,251,235,0.97);
          color: #78350f;
        }
        .ze-ann-pin { position: relative; cursor: pointer; }
        .ze-ann-pin-dot {
          width: 14px; height: 14px;
          background: #f59e0b;
          border: 2.5px solid #92400e;
          border-radius: 50% 50% 50% 0;
          transform: rotate(-45deg);
          box-shadow: 0 2px 5px rgba(0,0,0,0.30);
        }
      `}</style>
      {/* Control bar ABOVE the map — labeled tools + status + view actions, no overlap. */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 border border-[var(--color-border)] border-b-0 bg-[var(--color-bg-surface)] px-2 py-1.5">
        <div className="flex border border-[var(--color-border)]">
          {modeBtn(
            "draw",
            <Pencil size={13} />,
            "Rysuj",
            "Rysuj (wybierz typ z palety)",
          )}
          {modeBtn(
            "edit",
            <Spline size={13} />,
            "Edytuj",
            "Edytuj wierzchołki",
          )}
          {modeBtn("drag", <Move size={13} />, "Przesuń", "Przesuń element")}
          {modeBtn(
            "remove",
            <Trash2 size={13} />,
            "Usuń",
            "Usuń element (klik)",
          )}
          {modeBtn(
            "label",
            <Type size={13} />,
            "Etykieta",
            "Dodaj lub edytuj etykietę elementu (klik)",
          )}
          {modeBtn(
            "params",
            <SlidersHorizontal size={13} />,
            "Parametry",
            "Ustaw parametry techniczne elementu — głębokość, grunt, średnica (klik)",
          )}
          {modeBtn(
            "retype",
            <Replace size={13} />,
            "Typ",
            "Zmień typ elementu na inny z tej samej grupy palety (klik)",
          )}
          {modeBtn(
            "annotate",
            <MessageSquare size={13} />,
            "Adnotacja",
            "Dodaj tekstowy komentarz w dowolnym miejscu na mapie",
          )}
          {modeBtn(
            "info",
            <Info size={13} />,
            "Działka",
            "Kliknij działkę, aby przejść do niej w panelu; przy włączonej warstwie zabytków klik pobiera też dane obiektu z zabytek.gov.pl",
          )}
          {modeBtn(
            "streetview",
            <PersonStanding size={13} />,
            "Street View",
            "Kliknij punkt, potem kierunek — otwiera Google Street View",
          )}
        </div>
        {mode === "retype" && (
          <button
            type="button"
            title="Narysuj prostokąt, aby zaznaczyć wszystkie elementy jednego typu w obszarze i zmienić im typ naraz"
            onClick={() => {
              setRetypeEditor(null);
              clearGroupRetype();
              setGroupSelect((v) => !v);
            }}
            className={`inline-flex items-center gap-1.5 px-2 py-1.5 border font-mono text-[11px] uppercase tracking-wider transition-colors ${
              groupSelect
                ? "border-amber-500 bg-amber-500 text-white"
                : "border-[var(--color-border)] text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-elevated)]"
            }`}
          >
            <BoxSelect size={13} />
            <span>Zaznacz obszarem</span>
          </button>
        )}
        <button
          type="button"
          title="Pokaż/ukryj etykiety z długością każdej linii"
          onClick={() => setShowLineLengths(!showLineLengths)}
          className={`inline-flex items-center gap-1.5 px-2 py-1.5 border border-[var(--color-border)] font-mono text-[11px] uppercase tracking-wider transition-colors ${
            showLineLengths
              ? "bg-[var(--color-primary)] text-[var(--color-text-inverse)]"
              : "text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-elevated)]"
          }`}
        >
          <Ruler size={13} />
          <span>Długości</span>
        </button>
        <span className="font-mono text-[11px] text-[var(--color-text-secondary)] min-w-0 truncate">
          {statusText()}
        </span>
        <div style={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          <div>
            {/* "Znajdź na mapie" — left-aligned in the toolbar row (same row as "Przybliż").
                Locate by parcel TERYT or by address depending on the selected mode; the result
                layer follows the panel open/close (see the effect above), and only "Lokalizuj"
                re-centers the map. */}
            {!locateOpen ? (
              <button
                type="button"
                onClick={() => setLocateOpen(true)}
                title="Znajdź działkę lub adres na mapie i przybliż do niego"
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 border border-[var(--color-border)] font-mono text-[11px] uppercase tracking-wider text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-elevated)] transition-colors"
              >
                <Search size={13} />
                <span>Znajdź na mapie</span>
              </button>
            ) : (
              <div className="flex flex-wrap items-center gap-1.5">
                {/* mode toggle: działka | adres */}
                <div className="flex border border-[var(--color-border)]">
                  {(["parcel", "address"] as const).map((m) => (
                    <button
                      key={m}
                      type="button"
                      onClick={() => {
                        setLocateMode(m);
                        setLocateQuery("");
                        setLocateError(null);
                      }}
                      className={`px-2.5 py-1.5 border-r border-[var(--color-border)] last:border-r-0 font-mono text-[11px] uppercase tracking-wider transition-colors ${
                        locateMode === m
                          ? "bg-[var(--color-primary)] text-[var(--color-text-inverse)]"
                          : "text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-elevated)]"
                      }`}
                    >
                      {m === "parcel" ? "Działka" : "Adres"}
                    </button>
                  ))}
                </div>
                <input
                  autoFocus
                  value={locateQuery}
                  onChange={(e) => setLocateQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") locate();
                    if (e.key === "Escape") setLocateOpen(false);
                  }}
                  placeholder={
                    locateMode === "parcel"
                      ? "np. 146501_8.0001.123/4"
                      : "np. Gdańsk, Długa 1"
                  }
                  title={
                    locateMode === "parcel"
                      ? "Identyfikator działki (TERYT) — np. 146501_8.0001.123/4"
                      : "Geokoder GUGiK — podaj miejscowość i ulicę z numerem"
                  }
                  className="w-56 min-w-0 border border-[var(--color-border)] bg-white px-2 py-1 font-mono text-[12px] focus:outline-none focus:border-[var(--color-primary)]"
                />
                <Button
                  size="sm"
                  onClick={locate}
                  loading={locateLoading}
                  disabled={!locateQuery.trim()}
                >
                  Lokalizuj
                </Button>
                {locateError && (
                  <span className="text-[10px] text-amber-600 normal-case">{locateError}</span>
                )}
                <button
                  type="button"
                  onClick={() => setLocateOpen(false)}
                  title="Zwiń"
                  aria-label="Zwiń wyszukiwanie"
                  className="shrink-0 text-[var(--color-text-muted)] hover:text-[var(--color-primary)] transition-colors"
                >
                  <X size={14} />
                </button>
              </div>
            )}
          </div>
          <div>
            <div className="ml-auto flex gap-1">
              <Button size="sm" variant="ghost" onClick={fitToDrawing}>
                Przybliż
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={openGeoportal}
                title="Otwórz Geoportal dla bieżącego widoku"
              >
                <ExternalLink size={13} className="mr-1" />
                Geoportal
              </Button>
              <Button size="sm" variant="ghost" onClick={clearDrawing}>
                Wyczyść
              </Button>
            </div>
          </div>
        </div>
      </div>

      {drawingLoadFailed && (
        <div className="shrink-0 flex items-start gap-1.5 px-2.5 py-2 bg-red-600 text-white font-mono text-[11px] tracking-wide">
          <span className="text-base leading-none shrink-0">⚠</span>
          <span>
            Nie udało się wczytać zapisanego rysunku — na mapie widać najwyżej jego część.
            Zapis jest zablokowany, żeby nie nadpisać trasy w bazie. Zgłoś ten przetarg
            (szczegóły w konsoli przeglądarki).
          </span>
        </div>
      )}

      {!drawingLoadFailed && emptyFeatureCount > 0 && (
        <div className="shrink-0 flex items-start gap-1.5 px-2.5 py-2 bg-amber-500 text-white font-mono text-[11px] tracking-wide">
          <span className="text-base leading-none shrink-0">?</span>
          <span>
            {emptyFeatureCount} element(ów) rysunku nie ma współrzędnych — pozostałość po
            usuniętych liniach. Nic nie rysują; najbliższy zapis usunie je z rysunku.
          </span>
        </div>
      )}

      <div className="relative flex-1 min-h-0">
        <div
          ref={containerRef}
          className="w-full border border-[var(--color-border)]"
          style={{
            background: "#fff",
            height: isFullscreen ? "100%" : "560px",
          }}
        />
        {scaleRatio && (
          <div className="absolute bottom-2 left-2 z-[1000] pointer-events-none font-mono text-[10px] bg-white/80 border border-[var(--color-border)] px-1.5 py-0.5 leading-none text-[var(--color-text-secondary)]">
            {scaleRatio}
          </div>
        )}
        {drawingLengthM != null && (
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 z-[1000] pointer-events-none font-mono text-[12px] font-bold bg-white/92 border border-emerald-400 px-3 py-1 leading-none text-emerald-800 shadow-sm">
            {fmtMeters(drawingLengthM)}
          </div>
        )}
        {paramsEditor && (
          <ParamsEditorPanel
            layer={paramsEditor.layer}
            elementType={typesByCode[layerTypeCode(paramsEditor.layer)]}
            defaults={takeoffDefaults ?? {}}
            onCommit={commitParam}
            onClose={() => setParamsEditor(null)}
          />
        )}
        {retypeEditor && (
          <RetypeEditorPanel
            title={`Zmień typ: ${
              typesByCode[layerTypeCode(retypeEditor.layer)]?.label ??
              (layerTypeCode(retypeEditor.layer) || "element")
            }`}
            kind={layerKind(retypeEditor.layer)}
            currentCode={layerTypeCode(retypeEditor.layer)}
            elementTypes={elementTypes}
            onSelect={commitRetype}
            onClose={() => setRetypeEditor(null)}
          />
        )}
        {groupRetype?.step === "target" && (
          <RetypeEditorPanel
            title={`Zaznaczono ${groupRetype.layers.length} szt.: ${
              typesByCode[groupRetype.code]?.label ?? (groupRetype.code || "bez typu")
            } — nowy typ`}
            kind={
              typesByCode[groupRetype.code]?.kind ?? layerKind(groupRetype.layers[0])
            }
            currentCode={groupRetype.code}
            elementTypes={elementTypes}
            onSelect={commitGroupRetype}
            onClose={clearGroupRetype}
          />
        )}
        {groupRetype?.step === "pickType" && (
          <div className="absolute top-2 left-1/2 z-[1100] w-96 -translate-x-1/2 border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-2 shadow-lg">
            <div className="mb-1.5 flex items-center justify-between">
              <span className="font-mono text-[10px] uppercase tracking-wider text-[var(--color-text-secondary)]">
                Zaznaczanie obszarem
              </span>
              <button
                className="text-[11px] text-[var(--color-text-tertiary)] hover:text-[var(--color-text-primary)]"
                onClick={clearGroupRetype}
              >
                Zamknij
              </button>
            </div>
            <p className="mb-1.5 text-[12px] text-[var(--color-text-secondary)]">
              W obszarze są elementy kilku typów — wybierz, który typ zaznaczyć:
            </p>
            <div className="flex max-h-[380px] flex-wrap gap-1.5 overflow-y-auto pr-1">
              {groupRetype.groups
                .slice()
                .sort(
                  (a, b) =>
                    (typesByCode[a.code]?.order ?? 9999) -
                    (typesByCode[b.code]?.order ?? 9999),
                )
                .map(({ code, layers }) => (
                  <button
                    key={code || "__none__"}
                    type="button"
                    title={`Zaznacz wszystkie elementy tego typu w obszarze (${layers.length} szt.)`}
                    onClick={() => selectGroupOfType(code, layers)}
                    className="flex items-center gap-1.5 border border-[var(--color-border)] px-2 py-1 font-mono text-[11px] text-[var(--color-text-secondary)] transition-colors hover:bg-[var(--color-bg-elevated)]"
                  >
                    <span
                      className="inline-flex w-6 shrink-0 items-center justify-center"
                      aria-hidden
                      dangerouslySetInnerHTML={{
                        __html: elementSwatchSvg(typesByCode[code]),
                      }}
                    />
                    <span className="text-left">
                      {typesByCode[code]?.label ?? (code || "bez typu")}
                    </span>
                    <span className="text-[var(--color-text-muted)]">
                      × {layers.length}
                    </span>
                  </button>
                ))}
            </div>
          </div>
        )}
        {groupRetype?.step === "empty" && (
          <div className="absolute top-2 left-1/2 z-[1100] w-96 -translate-x-1/2 border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-2 shadow-lg">
            <div className="mb-1.5 flex items-center justify-between">
              <span className="font-mono text-[10px] uppercase tracking-wider text-[var(--color-text-secondary)]">
                Zaznaczanie obszarem
              </span>
              <button
                className="text-[11px] text-[var(--color-text-tertiary)] hover:text-[var(--color-text-primary)]"
                onClick={clearGroupRetype}
              >
                Zamknij
              </button>
            </div>
            <p className="py-1 text-[12px] text-[var(--color-text-muted)]">
              W zaznaczonym obszarze nie ma narysowanych elementów.
            </p>
          </div>
        )}
        {labelEditor && (
          <div className="absolute top-2 left-1/2 -translate-x-1/2 z-[1100] w-80 border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-2 shadow-lg">
            <div className="mb-1 font-mono text-[10px] uppercase tracking-wider text-[var(--color-text-secondary)]">
              Etykieta elementu
            </div>
            <textarea
              autoFocus
              rows={3}
              maxLength={500}
              value={labelEditor.text}
              onChange={(e) =>
                setLabelEditor((cur) => (cur ? { ...cur, text: e.target.value } : cur))
              }
              onKeyDown={(e) => {
                if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) commitLabel(labelEditor.text);
                if (e.key === "Escape") setLabelEditor(null);
              }}
              placeholder={"Tekst etykiety — Enter zaczyna nową linię"}
              className="w-full resize-y border border-[var(--color-border)] bg-white px-2 py-1 font-mono text-[12px] leading-snug focus:outline-none focus:border-[var(--color-primary)]"
            />
            <div className="mt-1.5 flex items-center gap-1.5">
              <Button size="sm" onClick={() => commitLabel(labelEditor.text)}>
                Zapisz etykietę
              </Button>
              {layerLabel(labelEditor.layer) !== "" && (
                <Button size="sm" variant="ghost" onClick={() => commitLabel("")}>
                  Usuń etykietę
                </Button>
              )}
              <Button size="sm" variant="ghost" onClick={() => setLabelEditor(null)}>
                Anuluj
              </Button>
            </div>
          </div>
        )}
        {annotationEditor && (
          <div className="absolute top-2 left-1/2 -translate-x-1/2 z-[1100] w-88 border border-amber-300 bg-amber-50 p-2 shadow-lg">
            <div className="mb-1 font-mono text-[10px] uppercase tracking-wider text-amber-700">
              {annotationEditor.kind === "new" ? "Nowa adnotacja" : "Edytuj adnotację"}
            </div>
            <textarea
              autoFocus
              rows={4}
              maxLength={2000}
              value={annotationEditor.text}
              onChange={(e) =>
                setAnnotationEditor((cur) =>
                  cur ? { ...cur, text: e.target.value } : cur,
                )
              }
              onKeyDown={(e) => {
                if (e.key === "Enter" && (e.ctrlKey || e.metaKey))
                  commitAnnotation(annotationEditor.text);
                if (e.key === "Escape") setAnnotationEditor(null);
              }}
              placeholder="Tekst komentarza — Enter zaczyna nową linię, Ctrl+Enter zatwierdza"
              className="w-full resize-y border border-amber-300 bg-white px-2 py-1 font-mono text-[12px] leading-snug focus:outline-none focus:border-amber-500"
            />
            <div className="mt-1.5 flex items-center gap-1.5">
              <Button
                size="sm"
                disabled={annotationEditor.kind === "new" && annotationEditor.saving}
                onClick={() => commitAnnotation(annotationEditor.text)}
              >
                {annotationEditor.kind === "new" ? "Dodaj" : "Zapisz"}
              </Button>
              {annotationEditor.kind === "edit" && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => deleteAnnotation(annotationEditor.annotation.id)}
                >
                  Usuń
                </Button>
              )}
              <Button size="sm" variant="ghost" onClick={() => setAnnotationEditor(null)}>
                Anuluj
              </Button>
            </div>
          </div>
        )}
        <GugikLayerPanel
          gugik={gugik}
          onChange={setGugik}
          zoom={zoom}
          loading={gugikLoading}
          className="absolute top-[78px] left-2"
          identifyHint="Tryb „Działka” pobiera dane klikniętego zabytku z zabytek.gov.pl."
          nidEmpty={{ rejestr: rejestrEmpty, ewidencja: ewidencjaEmpty }}
        />
        <ZEAnnotationsPanel
          annotations={annotations}
          onFlyTo={(id) => {
            const marker = annotationMarkersRef.current.get(id);
            if (marker && mapRef.current) {
              mapRef.current.setView(
                marker.getLatLng(),
                Math.max(mapRef.current.getZoom(), 16),
                { animate: true },
              );
            }
          }}
          onEdit={(ann) => setAnnotationEditor({ kind: "edit", annotation: ann, text: ann.text })}
          onDelete={(id) => onAnnotationDeleteRef.current?.(id)}
          className="absolute top-[78px] right-2"
        />
        {/* Fullscreen toggle (bottom-right of the map). In fullscreen a second
            "Zamknij" also lives in the top-right of the page (variant bar). */}
        <button
          type="button"
          onClick={onToggleFullscreen}
          title={isFullscreen ? "Zamknij pełny ekran" : "Pełny ekran"}
          className="absolute bottom-3 right-3 z-[1000] flex items-center gap-1.5 rounded-md bg-[var(--color-primary)] border border-[var(--color-primary)] shadow-md px-3 py-2 text-sm font-medium text-white hover:opacity-90 transition-opacity"
        >
          {isFullscreen ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
          <span>{isFullscreen ? "Zamknij" : "Pełny ekran"}</span>
        </button>
      </div>
    </div>
  );
});
